/**
 * SignatureFactoryRegistry - Registry Pattern for managing Signature Factories
 *
 * Singleton pattern kết hợp với Abstract Factory:
 * - Quản lý tất cả các factories (Viettel, VNPT, Easy, Oceantech)
 * - Cho phép switch giữa các providers dễ dàng
 * - Hỗ trợ đăng ký factory mới (extensible)
 *
 * Usage:
 *   // Lấy factory theo provider
 *   const factory = SignatureFactoryRegistry.getFactory(SignProvider.VIETTEL);
 *
 *   // Tạo signer theo type
 *   const signer = factory.createSigner(SignType.USB);
 *
 *   // Hoặc sử dụng shortcut
 *   const signer = SignatureFactoryRegistry.getSigner(SignType.USB, SignProvider.VIETTEL);
 */

import { ISignatureFactory } from './ISignatureFactory';
import { ISignatureProvider } from './signers/ISignatureProvider';
import { SignType, SignProvider, ProviderConfig, parseProviderCode } from './types';
import { GenericSignatureFactory } from './providers/GenericSignatureFactory';

/**
 * Factory Registry - Singleton
 *
 * Tự động switch provider dựa trên metadata field5:
 * - "MYSIGN-CA"   → MySign Remote CA
 * - "VIETTEL-USB" → Viettel USB Token
 * - "ICORP-HSM"   → iCorp HSM
 * - "VNPT-CA"     → VNPT Remote CA
 *
 * THÊM PROVIDER MỚI:
 * 1. Thêm enum vào SignProvider (types.ts)
 * 2. Thêm config vào providerConfigs.ts
 * 3. DONE! Không cần tạo class factory riêng
 */
class SignatureFactoryRegistryClass {
  private factories: Map<SignProvider, ISignatureFactory> = new Map();
  private defaultProvider: SignProvider = SignProvider.VIETTEL;
  private initialized: boolean = false;

  constructor() {
    // LAZY INITIALIZATION: Không đăng ký factories trong constructor
    // để tránh side effects khi import module
    // Factories sẽ được đăng ký khi cần thiết thông qua ensureInitialized()
  }

  /**
   * Lazy initialization - chỉ đăng ký factories khi thực sự cần
   * Tránh side effects khi import module
   */
  private ensureInitialized(): void {
    if (this.initialized) return;

    this.initialized = true;

    // Đăng ký factory cho tất cả providers trong enum
    // Sử dụng GenericSignatureFactory - không cần class riêng cho mỗi provider
    Object.values(SignProvider).forEach((provider) => {
      this.registerFactoryInternal(new GenericSignatureFactory(provider));
    });
  }

  /**
   * Internal method để đăng ký factory không trigger ensureInitialized
   */
  private registerFactoryInternal(factory: ISignatureFactory): void {
    this.factories.set(factory.provider, factory);
    console.log(`Registered signature factory: ${factory.displayName}`);
  }

  /**
   * Đăng ký một factory mới (public API)
   * Sẽ trigger lazy initialization nếu chưa được khởi tạo
   */
  registerFactory(factory: ISignatureFactory): void {
    this.ensureInitialized();
    this.factories.set(factory.provider, factory);
    console.log(`Registered custom signature factory: ${factory.displayName}`);
  }

  /**
   * Hủy đăng ký factory
   */
  unregisterFactory(provider: SignProvider): void {
    this.ensureInitialized();
    this.factories.delete(provider);
  }

  /**
   * Lấy factory theo provider
   */
  getFactory(provider: SignProvider): ISignatureFactory {
    this.ensureInitialized();
    const factory = this.factories.get(provider);
    if (!factory) {
      throw new Error(
        `Không tìm thấy factory cho provider: ${provider}. ` +
        `Các provider khả dụng: ${this.getAvailableProviders().join(', ')}`
      );
    }
    return factory;
  }

  /**
   * Lấy factory từ provider code string (từ metadata field5)
   * @param providerCode - Chuỗi như "MYSIGN-CA", "VIETTEL-USB", "ICORP-HSM", etc.
   */
  getFactoryFromProviderCode(providerCode: string): {
    factory: ISignatureFactory;
    signType: SignType;
    signProvider: SignProvider;
  } {
    const { type, provider } = parseProviderCode(providerCode);
    return {
      factory: this.getFactory(provider),
      signType: type,
      signProvider: provider,
    };
  }

  /**
   * Alias for backward compatibility
   * @deprecated Use getFactoryFromProviderCode instead
   */
  getFactoryFromLegacy = this.getFactoryFromProviderCode;

  /**
   * Tạo signer trực tiếp từ type và provider
   */
  getSigner(
    signType: SignType,
    provider: SignProvider,
    config?: Partial<ProviderConfig>
  ): ISignatureProvider {
    const factory = this.getFactory(provider);
    return factory.createSigner(signType, config);
  }

  /**
   * Tạo signer từ provider code string (từ metadata field5)
   * @param providerCode - Chuỗi như "MYSIGN-CA", "VIETTEL-USB", "ICORP-HSM"
   */
  getSignerFromProviderCode(
    providerCode: string,
    config?: Partial<ProviderConfig>
  ): ISignatureProvider {
    const { factory, signType } = this.getFactoryFromProviderCode(providerCode);
    return factory.createSigner(signType, config);
  }

  /**
   * Alias for backward compatibility
   * @deprecated Use getSignerFromProviderCode instead
   */
  getSignerFromLegacy = this.getSignerFromProviderCode;

  /**
   * Set default provider
   */
  setDefaultProvider(provider: SignProvider): void {
    this.ensureInitialized();
    if (!this.factories.has(provider)) {
      throw new Error(`Provider ${provider} chưa được đăng ký`);
    }
    this.defaultProvider = provider;
  }

  /**
   * Get default provider
   */
  getDefaultProvider(): SignProvider {
    return this.defaultProvider;
  }

  /**
   * Get default factory
   */
  getDefaultFactory(): ISignatureFactory {
    return this.getFactory(this.defaultProvider);
  }

  /**
   * Danh sách các provider khả dụng
   */
  getAvailableProviders(): SignProvider[] {
    this.ensureInitialized();
    return Array.from(this.factories.keys());
  }

  /**
   * Kiểm tra provider có được hỗ trợ không
   */
  hasProvider(provider: SignProvider): boolean {
    this.ensureInitialized();
    return this.factories.has(provider);
  }

  /**
   * Lấy tất cả factories
   */
  getAllFactories(): ISignatureFactory[] {
    this.ensureInitialized();
    return Array.from(this.factories.values());
  }

  /**
   * Kiểm tra một loại ký có được hỗ trợ bởi provider không
   */
  supportsSignType(signType: SignType, provider: SignProvider): boolean {
    this.ensureInitialized();
    const factory = this.factories.get(provider);
    return factory ? factory.supportsSignType(signType) : false;
  }

  /**
   * Lấy danh sách providers hỗ trợ một loại ký cụ thể
   */
  getProvidersForSignType(signType: SignType): SignProvider[] {
    return this.getAvailableProviders().filter((provider) =>
      this.supportsSignType(signType, provider)
    );
  }
}

// Export singleton instance
export const SignatureFactoryRegistry = new SignatureFactoryRegistryClass();

// Export type for external use
export type { SignatureFactoryRegistryClass };
